import { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Compass, Route, Users2, Check } from "lucide-react";
import { IMAGES } from "@/context/AppContext";
import { scrollToSection } from "@/components/SmoothScroll";

const API = process.env.REACT_APP_BACKEND_URL;

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-70px" }}
    transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

const QUESTIONS = [
  "What should they feel?",
  "What conversations should happen?",
  "Where should there be space to breathe?",
  "What moments should challenge them?",
  "What should they remember?",
  "What should they carry home?",
];

const OFFERINGS = [
  { title: "Retreats", body: "Immersive experiences built around your message, community, or curriculum." },
  { title: "Gatherings", body: "Purposeful live experiences designed for connection, storytelling, teaching, worship, conversation, or celebration." },
  { title: "Workshops + Intensives", body: "Interactive experiences that move an audience from listening into genuine participation." },
  { title: "Community Experiences", body: "Intentional experiences for memberships, ministries, counseling communities, organizations, and communities you lead." },
  { title: "Custom Concepts", body: "Have an idea that doesn't fit neatly? We build around the vision." },
];

const SUPPORT = [
  { icon: Compass, title: "Concept + Experience Design", body: "Clarifying the vision, theme, purpose, flow, and desired guest transformation." },
  { icon: Route, title: "Programming + Guest Journey", body: "Shaping the schedule, sessions, storytelling moments, activities, reflection, and participant experience." },
  { icon: Users2, title: "Coordination + Logistics", body: "Researching venues, organizing people and details, on-site support, meaningful touches — whatever the vision needs." },
];

const PARTNERS = ["Speakers", "Counselors + Coaches", "Authors", "Creators", "Ministries", "Churches", "Faith-led brands", "Community leaders", "Organizations"];

const PROCESS = [
  { n: "01", title: "Tell us the vision", body: "Share what you're dreaming about, who it's for, and what you want people to walk away with." },
  { n: "02", title: "We shape it together", body: "We translate the vision into an intentional concept, guest journey, and plan." },
  { n: "03", title: "We build", body: "Depending on the project, Outpoured helps coordinate the creative, logistical, and experiential pieces." },
  { n: "04", title: "They step into it", body: "The vision becomes something your people experience — not simply hear about." },
];

const EXPERIENCE_TYPES = ["Retreat", "Gathering", "Workshop + Intensive", "Community Experience", "Custom Concept", "Not sure yet"];

const input = "w-full bg-white border border-[#D8CEBE] rounded-md px-4 py-3 text-[#2A2825] font-body outline-none focus:border-[#A9724E] transition-colors";
const label = "block text-xs tracking-overline uppercase text-[#8a7a6a] mb-1.5";

function InquiryForm() {
  const [form, setForm] = useState({ name: "", email: "", organization: "", experience_type: "", vision: "", audience: "", timeframe: "", location: "", anything_else: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.vision.trim()) {
      toast.error("Please share your name, email, and a little about your vision.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/api/create-inquiry`, form);
      setDone(true);
      toast.success("Your inquiry is on its way. We'll be in touch soon.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally { setLoading(false); }
  };

  if (done) {
    return (
      <div data-testid="inquiry-success" className="bg-white/70 border border-[#E0D8C9] rounded-2xl p-8 md:p-10 text-center">
        <div className="w-12 h-12 rounded-full bg-[#A9724E] text-[#F4EFE6] flex items-center justify-center mx-auto mb-5"><Check className="w-6 h-6" strokeWidth={2} /></div>
        <h3 className="font-serif-display text-2xl md:text-3xl font-light mb-3">We received your vision.</h3>
        <p className="text-[#5E4B3C] font-body max-w-md mx-auto">Thank you for trusting us with what you're dreaming about. We read every inquiry personally and will reach out soon to start the conversation. Check your inbox for a note from us.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="inquiry-form" className="bg-white/70 border border-[#E0D8C9] rounded-2xl p-5 sm:p-6 md:p-10 space-y-5">
      <div className="grid md:grid-cols-2 gap-5">
        <div><label className={label}>Your name *</label><input data-testid="inquiry-name" required className={input} value={form.name} onChange={(e) => set("name", e.target.value)} /></div>
        <div><label className={label}>Email *</label><input data-testid="inquiry-email" type="email" required className={input} value={form.email} onChange={(e) => set("email", e.target.value)} /></div>
        <div><label className={label}>Organization / community (optional)</label><input data-testid="inquiry-organization" className={input} value={form.organization} onChange={(e) => set("organization", e.target.value)} /></div>
        <div><label className={label}>Type of experience</label>
          <select data-testid="inquiry-type" className={input} value={form.experience_type} onChange={(e) => set("experience_type", e.target.value)}>
            <option value="">Select…</option>
            {EXPERIENCE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      </div>
      <div><label className={label}>Tell us the vision *</label>
        <textarea data-testid="inquiry-vision" required rows={4} className={input} value={form.vision} onChange={(e) => set("vision", e.target.value)}
          placeholder="What are you dreaming about? What do you want people to walk away carrying?" />
      </div>
      <div className="grid md:grid-cols-2 gap-5">
        <div><label className={label}>Who is it for? (optional)</label><input data-testid="inquiry-audience" className={input} value={form.audience} onChange={(e) => set("audience", e.target.value)} placeholder="Your community, members, guests…" /></div>
        <div><label className={label}>Ideal timing (optional)</label><input data-testid="inquiry-timeframe" className={input} value={form.timeframe} onChange={(e) => set("timeframe", e.target.value)} placeholder="A season, a date, or 'still exploring'" /></div>
        <div><label className={label}>Location (optional)</label><input data-testid="inquiry-location" className={input} value={form.location} onChange={(e) => set("location", e.target.value)} /></div>
      </div>
      <div><label className={label}>Anything else? (optional)</label>
        <textarea data-testid="inquiry-anything" rows={3} className={input} value={form.anything_else} onChange={(e) => set("anything_else", e.target.value)} />
      </div>
      <button type="submit" disabled={loading} data-testid="inquiry-submit"
        className="group inline-flex items-center gap-2 rounded-full bg-[#A9724E] hover:bg-[#925C3B] text-[#F4EFE6] px-8 py-3.5 text-sm tracking-wide transition-colors duration-500 disabled:opacity-50">
        {loading ? "Sending…" : "Share your vision"} <ArrowRight className="w-4 h-4 transition-transform duration-500 group-hover:translate-x-1" strokeWidth={1.5} />
      </button>
    </form>
  );
}

export default function CreateWithUs() {
  const formRef = useRef(null);
  const navigate = useNavigate();
  useEffect(() => {
    document.title = "Created by Outpoured · A partnership studio";
    window.scrollTo(0, 0);
  }, []);
  const toForm = () => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  const goExperiences = (e) => {
    e.preventDefault();
    navigate("/");
    let tries = 0;
    const iv = setInterval(() => {
      tries += 1;
      if (document.getElementById("experiences")) { scrollToSection("experiences"); clearInterval(iv); }
      if (tries > 25) clearInterval(iv);
    }, 120);
  };

  return (
    <div data-testid="create-with-us" className="bg-[#F4EFE6] text-[#2A2825]">
      {/* HERO */}
      <section className="relative min-h-[78vh] md:min-h-[86vh] flex items-end grain overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.clayHands} alt="Hands shaping clay on a wheel" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#211E1B] via-[#211E1B]/70 to-[#211E1B]/25" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pb-14 md:pb-24 w-full">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }}
            className="tracking-[0.3em] md:tracking-[0.35em] uppercase text-[10px] md:text-xs text-[#E8B486] mb-4 md:mb-5">Created by Outpoured · A partnership studio</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.28 }}
            className="font-serif-display font-light text-[#F4EFE6] text-4xl sm:text-5xl lg:text-[4rem] leading-[1.08] max-w-4xl">
            You carry the vision. <span className="italic text-[#E8B486]">Let's bring it to life.</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.42 }}
            className="mt-5 md:mt-6 text-[#D9CFC2] font-body text-base md:text-xl leading-relaxed max-w-2xl">
            You know what you want people to walk away carrying. We help create the experience that gets them there.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.56 }}>
            <button data-testid="hero-start-conversation" onClick={toForm}
              className="group mt-8 md:mt-9 inline-flex items-center gap-2 rounded-full bg-[#A9724E] hover:bg-[#925C3B] text-[#F4EFE6] px-7 md:px-8 py-3.5 md:py-4 text-sm tracking-wide transition-colors duration-500">
              Start a conversation <ArrowRight className="w-4 h-4 transition-transform duration-500 group-hover:translate-x-1" strokeWidth={1.5} />
            </button>
          </motion.div>
        </div>
      </section>

      {/* MORE THAN AN EVENT */}
      <section className="py-16 md:py-32 border-b border-[#D1C9BE]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Reveal><p className="tracking-overline text-xs uppercase text-[#A9724E] mb-4 md:mb-5">More than an event</p></Reveal>
          <Reveal delay={0.06}>
            <h2 className="font-serif-display font-light text-3xl sm:text-4xl lg:text-5xl leading-[1.12] mb-6 md:mb-8">
              At Outpoured, we believe your vision deserves more than logistics.
            </h2>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="font-serif-display text-xl md:text-2xl italic text-[#A9724E] mb-6 md:mb-8">A beautiful room isn't enough.</p>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="text-[#5E4B3C] leading-relaxed font-body text-base md:text-lg mb-8">
              We think about what people experience from the moment they arrive to the moment they leave:
            </p>
          </Reveal>
          <div className="grid sm:grid-cols-2 gap-x-10 gap-y-3.5 md:gap-y-4 mb-8">
            {QUESTIONS.map((q, i) => (
              <Reveal key={q} delay={0.05 * i}>
                <p className="font-serif-display text-lg md:text-2xl text-[#2A2825] leading-snug flex gap-3">
                  <span className="text-[#A9724E]">—</span>{q}
                </p>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <p className="text-[#5E4B3C] leading-relaxed font-body text-base md:text-lg">We help connect those pieces into one intentional experience.</p>
          </Reveal>
        </div>
      </section>

      {/* WHAT WE CREATE */}
      <section className="py-16 md:py-32 bg-[#EAE3D9]/40 border-b border-[#D1C9BE]">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <Reveal><p className="tracking-overline text-xs uppercase text-[#A9724E] mb-4 md:mb-5">What we create</p></Reveal>
          <Reveal delay={0.06}>
            <p className="font-serif-display font-light text-2xl md:text-3xl leading-snug mb-10 md:mb-14 max-w-3xl">
              Whether you're envisioning something intimate or expansive, we shape experiences around how people actually transform:
            </p>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-x-12 gap-y-8 md:gap-y-10">
            {OFFERINGS.map((o, i) => (
              <Reveal key={o.title} delay={0.05 * i}>
                <div className="border-t border-[#D1C9BE] pt-5">
                  <h3 className="font-serif-display text-2xl md:text-3xl font-light mb-2">{o.title}</h3>
                  <p className="text-[#5E4B3C] leading-relaxed font-body">{o.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* HOW WE SUPPORT YOU */}
      <section className="py-16 md:py-32 border-b border-[#D1C9BE]">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <Reveal><p className="tracking-overline text-xs uppercase text-[#A9724E] mb-4 md:mb-5">How we support you</p></Reveal>
          <Reveal delay={0.06}><h2 className="font-serif-display font-light text-3xl sm:text-4xl lg:text-5xl leading-tight mb-10 md:mb-14 max-w-2xl">Wherever the vision needs hands.</h2></Reveal>
          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {SUPPORT.map((s, i) => (
              <Reveal key={s.title} delay={0.05 * i}>
                <div className="h-full bg-white/60 border border-[#E0D8C9] rounded-2xl p-6 md:p-7">
                  <s.icon className="w-6 h-6 text-[#A9724E] mb-4 md:mb-5" strokeWidth={1.25} />
                  <h3 className="font-serif-display text-xl md:text-2xl font-light mb-2.5">{s.title}</h3>
                  <p className="text-[#5E4B3C] leading-relaxed font-body text-sm">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15}>
            <p className="text-[#5E4B3C] font-body italic text-base md:text-lg mt-8 md:mt-10 max-w-2xl">
              Every project is different. We customize support based on your needs, not a fixed package.
            </p>
          </Reveal>
        </div>
      </section>

      {/* WHO WE CREATE WITH */}
      <section className="py-16 md:py-32 bg-[#2A2825] text-[#F4EFE6] grain relative">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Reveal><p className="tracking-overline text-xs uppercase text-[#E8B486] mb-4 md:mb-5">Who we create with</p></Reveal>
          <Reveal delay={0.06}>
            <h2 className="font-serif-display font-light text-3xl sm:text-4xl lg:text-5xl leading-tight mb-8 md:mb-10">
              For the ones entrusted with a message.
            </h2>
          </Reveal>
          <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-3 mb-10 md:mb-12">
            {PARTNERS.map((p, i) => (
              <Reveal key={p} delay={0.03 * i}>
                <span className="inline-block rounded-full border border-[#E8B486]/40 text-[#D9CFC2] font-body text-sm px-4 md:px-5 py-2 md:py-2.5">{p}</span>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <div className="text-[#D9CFC2] font-body text-base md:text-lg leading-relaxed max-w-2xl mx-auto space-y-4">
              <p>Maybe you already have the message. Maybe you already have the audience. Maybe the retreat has lived in your notes for two years.</p>
              <p className="text-[#F4EFE6]">You don't have to figure out every detail alone.</p>
              <p>Bring us what you're carrying. We'll help you think through what it could become.</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* THE PROCESS */}
      <section className="py-16 md:py-32 border-b border-[#D1C9BE]">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <Reveal><p className="tracking-overline text-xs uppercase text-[#A9724E] mb-4 md:mb-5">The process</p></Reveal>
          <Reveal delay={0.06}><h2 className="font-serif-display font-light text-3xl sm:text-4xl lg:text-5xl leading-tight mb-10 md:mb-16 max-w-2xl">From a single vision to something they step into.</h2></Reveal>
          <div className="space-y-7 md:space-y-10">
            {PROCESS.map((p, i) => (
              <Reveal key={p.n} delay={0.05 * i}>
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-8 border-t border-[#D1C9BE] pt-5 md:pt-6">
                  <span className="font-serif-display text-3xl md:text-4xl text-[#A9724E] font-light shrink-0 sm:w-16">{p.n}</span>
                  <div>
                    <h3 className="font-serif-display text-2xl md:text-3xl font-light mb-2">{p.title}</h3>
                    <p className="text-[#5E4B3C] leading-relaxed font-body max-w-2xl">{p.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* INQUIRY FORM */}
      <section ref={formRef} className="py-16 md:py-32 bg-[#EAE3D9]/40 scroll-mt-16 border-b border-[#D1C9BE]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <Reveal><p className="tracking-overline text-xs uppercase text-[#A9724E] mb-3 md:mb-4 text-center">Let's talk</p></Reveal>
          <Reveal delay={0.06}><h2 className="font-serif-display font-light text-3xl sm:text-4xl lg:text-5xl leading-tight mb-4 text-center">Tell us what you're dreaming about.</h2></Reveal>
          <Reveal delay={0.1}><p className="text-[#5E4B3C] font-body text-center max-w-xl mx-auto mb-10 md:mb-12">Share as much or as little as you'd like. We read every inquiry personally and will reach out to start the conversation.</p></Reveal>
          <Reveal delay={0.14}><InquiryForm /></Reveal>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 md:py-32">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <Reveal><h2 className="font-serif-display font-light text-3xl sm:text-4xl lg:text-5xl leading-tight mb-6">What have you been wanting to create?</h2></Reveal>
          <Reveal delay={0.08}>
            <p className="text-[#5E4B3C] font-body text-base md:text-lg leading-relaxed max-w-xl mx-auto mb-9">
              Maybe it's a retreat. Maybe it's a gathering. Maybe it's something that doesn't have a name yet. Let's talk about what it could become.
            </p>
          </Reveal>
          <Reveal delay={0.14}>
            <button data-testid="final-cta-button" onClick={toForm}
              className="group inline-flex items-center gap-2 rounded-full bg-[#A9724E] hover:bg-[#925C3B] text-[#F4EFE6] px-9 py-4 text-sm tracking-wide transition-colors duration-500">
              Bring your vision <ArrowRight className="w-4 h-4 transition-transform duration-500 group-hover:translate-x-1" strokeWidth={1.5} />
            </button>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="text-sm text-[#8a7a6a] font-body mt-7">
              Gathering with us instead?{" "}
              <Link data-testid="final-cta-experiences" to="/" onClick={goExperiences}
                className="text-[#A9724E] underline underline-offset-4 hover:text-[#925C3B]">Explore Outpoured Experiences.</Link>
            </p>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
